import GeoLocationComponent from 'components/GeoLocationComponent/GeoLocationComponent';
import { RESOURCE_SET_STATUSES } from 'components/IAP/ICSConstants';
import { useGetRosterForIncident } from 'hooks/incident/useRoster';
import {
  useResourceRelationships,
  useUpdateResourceRelationships,
} from 'hooks/resources/useResourceRelationships';
import React, { useState } from 'react';
import { Form, Offcanvas, Row } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import DrawerWrapper, { DrawerFooter } from './DrawerWrapper';
import { EditEntityDrawerProps } from './EntityListProps';
import ResourceSelectFields from './ResourceSelectFields';
import FormDateTimeField from './formFields/FormDateTimeField';
import FormSelectField from './formFields/FormSelectField';
import FormTextAreaField from './formFields/FormTextAreaField';
import FormTextField from './formFields/FormTextField';
import RelationshipPickerField from './formFields/RelationshipPickerField';
import { useAllIncidents } from './hooks/incidentHooks';
import { useCustomResourcePersistence } from './hooks/customResourceTypeHooks';
import {
  EditResourceSetFormData,
  ResourceSet,
  useUpdateResourceSet,
} from './hooks/resourceSetHooks';
import { useResourceSites } from './hooks/resourceSiteHooks';

// Correctly typed RootState
interface RootState {
  app: {
    incidents: Array<{ id: string; name: string }>;
    currentIncident: { id: string; name: string };
  };
}

const ResourceSetEditDrawer: React.FC<EditEntityDrawerProps<ResourceSet>> = ({
  toggle,
  selectedEntity: editedEntity,
}) => {
  const form = useForm<EditResourceSetFormData>({
    mode: 'onChange',
    defaultValues: {
      id: editedEntity.id,
      resourceSetId: editedEntity.resource_set_id,
      resourceSiteId: editedEntity.resource_site_id,
      groupGuid: editedEntity.group_guid,
      name: editedEntity.name,
      description: editedEntity.description,
      geolocation: editedEntity.geolocation,
      kind: editedEntity.kind,
      type: editedEntity.type,
      quantity: editedEntity.quantity,
      status: editedEntity.status,
      consolidationTargetId: editedEntity.consolidation_target_id,
      archived: editedEntity.archived,
      dateTimeOrdered: editedEntity.date_time_ordered,
      eta: editedEntity.eta,
      incidents: editedEntity.incidents,
      members: [],
      sourceLocation: editedEntity.source_location,
      sourceResourceSiteId: editedEntity.source_resource_site_id,
      destinationLocation: editedEntity.destination_location,
      destinationResourceSiteId: editedEntity.destination_resource_site_id,
    },
  });
  const { setValue, watch } = form;
  const watchedStatus = watch('status');

  const { data: resourceSites } = useResourceSites();

  const mutation = useUpdateResourceSet(editedEntity.resource_set_id);
  const [geolocation, setGeolocation] = useState(editedEntity.geolocation);
  const [sourceLocation, setSourceLocation] = useState(
    editedEntity.source_location
  );
  const [destinationLocation, setDestinationLocation] = useState(
    editedEntity.destination_location
  );
  const { persistPendingResources } = useCustomResourcePersistence();

  // Location type toggles - initialize based on existing data
  const [useCustomCurrentLocation, setUseCustomCurrentLocation] = useState(
    !!editedEntity.geolocation
  );
  const [useCustomSourceLocation, setUseCustomSourceLocation] = useState(
    !!editedEntity.source_location
  );
  const [useCustomDestinationLocation, setUseCustomDestinationLocation] =
    useState(!!editedEntity.destination_location);

  // Fetch incidents and current incident with proper types
  const { data: incidents = [] } = useAllIncidents();
  const currentIncident = useSelector(
    (state: RootState) => state.app.currentIncident
  );

  // Fetch incident members and relationships
  const { data: rosterData = [] } = useGetRosterForIncident(
    currentIncident?.id
  );
  const { data: relationships = [] } = useResourceRelationships(
    editedEntity.resource_set_id,
    'resource'
  );
  const updateRelationships = useUpdateResourceRelationships();

  // Set the default incidents value
  React.useEffect(() => {
    if (editedEntity.incidents && editedEntity.incidents.length > 0) {
      setValue('incidents', editedEntity.incidents);
    } else if (currentIncident?.id) {
      setValue('incidents', [currentIncident.id]);
    }
  }, [currentIncident, editedEntity.incidents, setValue]);

  // Set default members value from relationships
  React.useEffect(() => {
    if (relationships.length > 0) {
      setValue(
        'members',
        relationships.map((r) => r.id)
      );
    }
  }, [relationships, setValue]);

  const onSubmit = async (formData: EditResourceSetFormData) => {
    await persistPendingResources(form.watch('kind'), form.watch('type'));
    await submitResourceSetEdit(formData);
  };

  const submitResourceSetEdit = async (formData: EditResourceSetFormData) => {
    const payload = {
      ...formData,
      resourceSetId: editedEntity.resource_set_id,
      // Current location - either custom or site, not both
      geolocation: useCustomCurrentLocation ? geolocation : null,
      resourceSiteId: useCustomCurrentLocation ? null : formData.resourceSiteId,
      // Source location - either custom or site, not both
      sourceLocation: useCustomSourceLocation ? sourceLocation : null,
      sourceResourceSiteId: useCustomSourceLocation
        ? null
        : formData.sourceResourceSiteId,
      // Destination location - either custom or site, not both
      destinationLocation: useCustomDestinationLocation
        ? destinationLocation
        : null,
      destinationResourceSiteId: useCustomDestinationLocation
        ? null
        : formData.destinationResourceSiteId,
      incidents: editedEntity.incidents, // Keep existing incidents - don't allow modification
    };

    // First save the resource
    await mutation.mutateAsync(payload);

    // Then update the member relationships if they've changed
    if (formData.members) {
      await updateRelationships.mutateAsync({
        entityId: editedEntity.resource_set_id,
        entityType: 'resource',
        relatedIds: formData.members,
      });
    }

    toggle();
  };

  const toggleActive = async () => {
    const newArchivedValue = !editedEntity.archived;
    // Convert snake_case entity to camelCase for the mutation
    const payload: EditResourceSetFormData = {
      id: editedEntity.id,
      resourceSetId: editedEntity.resource_set_id,
      resourceSiteId: editedEntity.resource_site_id,
      groupGuid: editedEntity.group_guid,
      name: editedEntity.name,
      description: editedEntity.description,
      geolocation: editedEntity.geolocation,
      kind: editedEntity.kind,
      type: editedEntity.type,
      quantity: editedEntity.quantity,
      status: editedEntity.status,
      consolidationTargetId: editedEntity.consolidation_target_id,
      archived: newArchivedValue,
      dateTimeOrdered: editedEntity.date_time_ordered,
      eta: editedEntity.eta,
      incidents: editedEntity.incidents,
      members: [],
      sourceLocation: editedEntity.source_location,
      sourceResourceSiteId: editedEntity.source_resource_site_id,
      destinationLocation: editedEntity.destination_location,
      destinationResourceSiteId: editedEntity.destination_resource_site_id,
    };
    await mutation.mutateAsync(payload);
    toggle();
  };

  return (
    <DrawerWrapper toggle={toggle} title="Edit Resource">
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="d-flex flex-column justify-content-between flex-grow-1"
        >
          <Offcanvas.Body>
            <Row>
              <FormTextField
                title="Name"
                fieldName="name"
                rules={{
                  required: { value: true, message: 'This field is required' },
                  maxLength: {
                    value: 300,
                    message: 'This maximum length is 300 characters',
                  },
                }}
                disabled={editedEntity.is_external_resource}
                variant="half"
              />
            </Row>
            <Row>
              <FormTextAreaField
                title="Description"
                fieldName="description"
                variant="full"
                disabled={editedEntity.is_external_resource}
              />
            </Row>
            <ResourceSelectFields
              labels
              disabled={editedEntity.is_external_resource}
            />
            <Row>
              <FormTextField
                title="Quantity"
                fieldName="quantity"
                rules={{
                  required: { value: true, message: 'This field is required' },
                  maxLength: {
                    value: 300,
                    message: 'This maximum length is 300 characters',
                  },
                }}
                disabled={editedEntity.is_external_resource}
                variant="half"
              />
            </Row>
            <Row>
              <FormDateTimeField
                title="Date Time Ordered"
                fieldName="dateTimeOrdered"
                variant="half"
                type="datetime-local"
              />
            </Row>
            <Row>
              <FormDateTimeField
                title="ETA"
                fieldName="eta"
                variant="half"
                type="datetime-local"
              />
            </Row>
            <Row>
              <FormSelectField
                title="Set Status"
                fieldName="status"
                rules={{
                  required: { value: true, message: 'This field is required' },
                }}
                options={
                  RESOURCE_SET_STATUSES.map((u) => ({
                    label: u.label,
                    value: u.value,
                  })) || []
                }
                variant="half"
              />
            </Row>
            {/* Location fields - conditional based on status */}
            {(watchedStatus === 'In Transit' ||
              watchedStatus === 'Ordered') && (
              <>
                <Row>
                  <div className="mb-3">
                    <h6>Location Information</h6>
                  </div>
                </Row>

                {/* Source Location */}
                <Row className="mb-3">
                  <div className="col-12">
                    <Form.Check
                      type="switch"
                      id="use-custom-source-location"
                      label="Use Custom Source Location"
                      checked={useCustomSourceLocation}
                      onChange={(e) => {
                        setUseCustomSourceLocation(e.target.checked);
                        if (e.target.checked) {
                          setValue('sourceResourceSiteId', '');
                        } else {
                          setSourceLocation(undefined);
                        }
                      }}
                      className="mb-2"
                      disabled={editedEntity.is_external_resource}
                    />
                    {useCustomSourceLocation ? (
                      <div>
                        <label className="form-label">Source Location:</label>
                        <GeoLocationComponent
                          location={sourceLocation}
                          setLocation={setSourceLocation}
                        />
                      </div>
                    ) : (
                      <FormSelectField
                        title="Source Site"
                        fieldName="sourceResourceSiteId"
                        options={
                          resourceSites
                            ?.filter((r) => !r.archived)
                            ?.map((r) => ({ label: r.name, value: r.id })) || []
                        }
                        variant="full"
                      />
                    )}
                  </div>
                </Row>

                {/* Current Location */}
                <Row className="mb-3">
                  <div className="col-12">
                    <Form.Check
                      type="switch"
                      id="use-custom-current-location"
                      label="Use Custom Current Location"
                      checked={useCustomCurrentLocation}
                      onChange={(e) => {
                        setUseCustomCurrentLocation(e.target.checked);
                        if (e.target.checked) {
                          setValue('resourceSiteId', '');
                        } else {
                          setGeolocation(undefined);
                        }
                      }}
                      className="mb-2"
                      disabled={editedEntity.is_external_resource}
                    />
                    {useCustomCurrentLocation ? (
                      <div>
                        <label className="form-label">Current Location:</label>
                        <GeoLocationComponent
                          location={geolocation}
                          setLocation={setGeolocation}
                          readOnly={editedEntity.is_external_resource}
                        />
                      </div>
                    ) : (
                      <FormSelectField
                        title="Current Site"
                        fieldName="resourceSiteId"
                        options={
                          resourceSites
                            ?.filter((r) => !r.archived)
                            ?.map((r) => ({ label: r.name, value: r.id })) || []
                        }
                        variant="full"
                        disabled={editedEntity.is_external_resource}
                      />
                    )}
                  </div>
                </Row>

                {/* Destination Location */}
                <Row className="mb-3">
                  <div className="col-12">
                    <Form.Check
                      type="switch"
                      id="use-custom-destination-location"
                      label="Use Custom Destination Location"
                      checked={useCustomDestinationLocation}
                      onChange={(e) => {
                        setUseCustomDestinationLocation(e.target.checked);
                        if (e.target.checked) {
                          setValue('destinationResourceSiteId', '');
                        } else {
                          setDestinationLocation(undefined);
                        }
                      }}
                      disabled={editedEntity.is_external_resource}
                      className="mb-2"
                    />
                    {useCustomDestinationLocation ? (
                      <div>
                        <label className="form-label">
                          Destination Location:
                        </label>
                        <GeoLocationComponent
                          location={destinationLocation}
                          setLocation={setDestinationLocation}
                        />
                      </div>
                    ) : (
                      <FormSelectField
                        title="Destination Site"
                        fieldName="destinationResourceSiteId"
                        options={
                          resourceSites
                            ?.filter((r) => !r.archived)
                            ?.map((r) => ({ label: r.name, value: r.id })) || []
                        }
                        variant="full"
                      />
                    )}
                  </div>
                </Row>
              </>
            )}

            {/* Non-transit resources - only current location */}
            {watchedStatus &&
              watchedStatus !== 'In Transit' &&
              watchedStatus !== 'Ordered' && (
                <Row className="mb-3">
                  <div className="col-12">
                    <Form.Check
                      type="switch"
                      id="use-custom-current-location"
                      label="Use Custom Location"
                      checked={useCustomCurrentLocation}
                      onChange={(e) => {
                        setUseCustomCurrentLocation(e.target.checked);
                        if (e.target.checked) {
                          setValue('resourceSiteId', '');
                        } else {
                          setGeolocation(undefined);
                        }
                      }}
                      className="mb-2"
                      disabled={editedEntity.is_external_resource}
                    />
                    {useCustomCurrentLocation ? (
                      <div>
                        <label className="form-label">Current Location:</label>
                        <GeoLocationComponent
                          location={geolocation}
                          setLocation={setGeolocation}
                          readOnly={editedEntity.is_external_resource}
                        />
                      </div>
                    ) : (
                      <FormSelectField
                        title="Resource Site"
                        fieldName="resourceSiteId"
                        options={
                          resourceSites
                            ?.filter((r) => !r.archived)
                            ?.map((r) => ({ label: r.name, value: r.id })) || []
                        }
                        variant="full"
                      />
                    )}
                  </div>
                </Row>
              )}
            <Row>
              <div className="mb-3">
                <label className="form-label">Incidents</label>
                {editedEntity.incidents && editedEntity.incidents.length > 0 ? (
                  <ul className="list-group">
                    {editedEntity.incidents.map((incidentId) => {
                      const incident = incidents.find(
                        (i) => i.id === incidentId
                      );
                      return (
                        <li key={incidentId} className="list-group-item">
                          {incident?.name || incidentId}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="text-muted">
                    <small>
                      No incidents assigned. Resources will be automatically
                      added when assigned to incidents.
                    </small>
                  </div>
                )}
              </div>
            </Row>
            <Row>
              <div className="text-muted">
                <small>
                  The available members are those that are found in the roster
                  of the currently selected incident. The currently selected
                  incident is {currentIncident?.name}
                </small>
              </div>
              <RelationshipPickerField
                label="Assigned Members"
                fieldName="members"
                defaultValue={relationships.map((r) => r.id)}
                options={
                  rosterData?.map((member) => ({
                    ...member,
                    id: member.incidentMemberId,
                  })) || []
                }
                identity={(member) => member.id}
                title={(member) => member.emailAddress || member.userName || ''}
                cardType="incident-member"
              />
            </Row>
          </Offcanvas.Body>
          <DrawerFooter>
            <div className="button-group">
              <StylishNewButton
                className="button--tertiary button--reverse"
                onClick={toggleActive}
              >
                {(!editedEntity.archived && 'Deactivate') || 'Activate'}
              </StylishNewButton>
              <StylishNewButton
                className="button--secondary button--reverse"
                type="button"
                onClick={toggle}
              >
                Close
              </StylishNewButton>
              <StylishNewButton
                className="button--primary"
                type="submit"
                disabled={mutation.isPending}
              >
                Save
              </StylishNewButton>
            </div>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerWrapper>
  );
};

export default ResourceSetEditDrawer;
